/**
 * UpgradeDialog — the shared upsell dialog a 402 `entitlement_required`
 * raises anywhere in the app (see billingUpgradeInterceptor.js +
 * UpgradeDialogHost.js — this component never fetches anything itself; it
 * is purely driven by the `details` prop the interceptor extracted from the
 * refusal that triggered it).
 *
 * TWO VARIANTS, driven by data, not a hardcoded branch per surface: when
 * `details.freeAllowance` is a number the refusal was METERED (exam-start /
 * tailored-practice) and the dialog leads with the actual meter numbers
 * ("You've used N of M free full-length tests"); when it is `null` the
 * refusal was PURE PREMIUM (the study plan today; any future
 * `enforce_premium_feature` surface automatically renders the same way,
 * since this branches on the DATA shape, never on which endpoint fired —
 * see billing_entitlements.py `_upgrade_refusal`). The reasoning prose below
 * the meter line, and the next-step bullets, are the BACKEND's own message/
 * next_steps rendered verbatim (never a second, competing hardcoded line) —
 * the backend already writes warm, growth-mindset copy naming what the
 * student KEEPS before what they'd gain.
 *
 * ACCESSIBILITY: built on the shared <Modal> primitive (focus trap + Escape
 * + restore-focus + initial-focus — see Modal.js). Initial focus lands on
 * the DISMISS action ("Keep practicing free"), never the upgrade CTA —
 * mirrors ConfirmDialog's safe-by-default reasoning: a reflexive Enter
 * right after the dialog opens should never start a purchase flow.
 *
 * NEVER A DEAD END: every render path ends in either the CheckoutFlow
 * button (a real next step) or an explicit "Keep practicing free" dismiss
 * that closes back to wherever the student already was (a real page, not a
 * void) — never a bare close-with-nothing-else.
 *
 * Props:
 *   details    { code, message, nextSteps, freeAllowance, freeUsed } — see
 *              billingUpgradeInterceptor.js for how these are populated.
 *   onClose    () => void.
 */
import { useRef } from 'react';
import { Modal, Button } from '../components/ui';
import CheckoutFlow from './CheckoutFlow';
import './UpgradeDialog.css';

export default function UpgradeDialog({ details, onClose }) {
  const dismissRef = useRef(null);
  const { message, nextSteps = [], freeAllowance, freeUsed } = details || {};
  const metered = typeof freeAllowance === 'number';

  return (
    <Modal
      labelledBy="upgrade-dialog-title"
      describedBy="upgrade-dialog-desc"
      onClose={onClose}
      initialFocusRef={dismissRef}
      overlayClassName="ui-modal-overlay upgrade-dialog-overlay"
      className="ui-modal upgrade-dialog"
    >
      <h2 id="upgrade-dialog-title" className="upgrade-dialog__title">
        Go Premium
      </h2>

      <div id="upgrade-dialog-desc" className="upgrade-dialog__body">
        {metered && (
          <p className="upgrade-dialog__meter">
            You&rsquo;ve used <strong>{freeUsed}</strong> of{' '}
            <strong>{freeAllowance}</strong> free full-length practice test
            {freeAllowance === 1 ? '' : 's'}.
          </p>
        )}
        {message && <p className="upgrade-dialog__message">{message}</p>}
        {nextSteps.length > 0 && (
          <ul className="upgrade-dialog__steps">
            {nextSteps.map((step, i) => (
              <li key={i}>{step}</li>
            ))}
          </ul>
        )}
      </div>

      <div className="upgrade-dialog__checkout">
        {/* onDismiss={onClose}, not an auto-close on the terminal phases:
            the already_entitled celebration ("Manage billing" / "Great,
            thanks") and every other result state must stay ON SCREEN until
            the student explicitly dismisses it — closing the whole dialog
            the instant a refusal resolves would hide that state before
            anyone could read it. */}
        <CheckoutFlow onDismiss={onClose} />
      </div>

      <div className="upgrade-dialog__actions">
        <Button ref={dismissRef} variant="ghost" onClick={onClose}>
          Keep practicing free
        </Button>
      </div>
    </Modal>
  );
}
